import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useNavigationsQuery } from '@data/navigations/useNavigationsQuery';
import { useMainSponsorQuery } from '@data/sponsors/useMainSponsorQuery';
import { useDrawerSearch } from './useDrawerSearch';
import { trackNavBurgerMenu } from '@ui/utils/tracking';
import { GlobalSearch } from '../GlobalSearch';
import { Logo } from '../Logo';
import { MainSponsorLogo } from '../MainSponsorLogo';
import type { ActiveNavigationNode } from './useActiveNav';
import { linkTargetProps, toHref, useActiveNav } from './useActiveNav';

const BAND_ITEM_COUNT = 3;

/** Lets the burger name the surface it opens. */
const MOBILE_DRAWER_ID = 'mobile-nav-drawer';

function navItemColorStyle(color: string | null | undefined): CSSProperties | undefined {
  return color ? ({ '--nav-item-color': color } as CSSProperties) : undefined;
}

/**
 * Escape closes the drawer.
 *
 * `GlobalSearch` has always documented this as `MobileNav`'s job — it skips its own Escape
 * handling for the drawer variant on the strength of it — and the handler was simply never
 * written, so the only way out of a full-screen overlay from a keyboard was to find the ✕.
 * `HeaderNav` carries the same listener for the mega panel.
 */
function useEscapeToClose(isOpen: boolean, close: () => void) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);
}

/**
 * Compact (<1440px) navigation: a solid band with the back button (on
 * sub-pages), the first 3 mobile items and a burger opening the right-side
 * accordion drawer.
 */
export function MobileNav({ className, isFlatDrawer }: { className?: string; isFlatDrawer?: boolean }) {
  const { data, isLoading, error } = useNavigationsQuery();
  const location = useLocation();
  const items = useActiveNav(data?.header?.mobileNodes);

  const [isOpen, setIsOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const search = useDrawerSearch();
  const resetSearch = search.reset;
  const burgerRef = useRef<HTMLButtonElement>(null);

  const toggleDrawer = useCallback(() => {
    trackNavBurgerMenu(isOpen ? 'close' : 'open');
    if (isOpen) {
      resetSearch();
    }
    setIsOpen((prev) => !prev);
  }, [isOpen, resetSearch]);

  const closeDrawer = useCallback(() => {
    if (isOpen) {
      trackNavBurgerMenu('close');
      resetSearch();
      setIsOpen(false);
      // Focus goes back to the control that opened the drawer. Without it, closing from the ✕
      // leaves focus on a button the stylesheet has just moved off screen and hidden, and the
      // next Tab restarts from the top of the document.
      //
      // Asserted rather than `?.`: every control that can reach this — the ✕, the scrim, Escape,
      // a link in the accordion — renders in the same subtree as the burger, so there is no state
      // where one is mounted without the other, and the optional branch would be one no test
      // could take.
      burgerRef.current!.focus();
    }
  }, [isOpen, resetSearch]);

  const toggleAccordion = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Close + reset accordion and search on route change. Clicking a result navigates, so without the
  // search reset the drawer would reopen on the next page still showing the previous search.
  useEffect(() => {
    setIsOpen(false);
    setExpandedIds(new Set());
    resetSearch();
  }, [location.pathname, resetSearch]);

  // Body scroll lock while the drawer is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEscapeToClose(isOpen, closeDrawer);

  if (isLoading || error || items.length === 0) {
    return null;
  }

  const navClassName = className ? `nav nav--band ${className}` : 'nav nav--band';

  return (
    <nav className={navClassName}>
      <BandBackButton />
      <ul className="nav__list">
        {items.slice(0, BAND_ITEM_COUNT).map((item) => (
          <li className="nav__list-item" key={item.id}>
            <BandLink item={item} />
          </li>
        ))}
      </ul>
      {/* The band carries no search icon of its own. It used to, and on a phone that put two ways
          into the same surface a thumb-width apart — the icon and the burger both opened the
          drawer, one with the field focused and one without. The drawer's own field is the entry
          point now, which is also what keeps the band's three nav links readable on a narrow
          screen. Desktop still opens search from the header trigger; that is `HeaderNav`. */}
      {/* One stable name, with `aria-expanded` carrying the state — the ARIA disclosure pattern.
          It used to flip to "Close menu" on open, which is the drawer ✕'s name too: two controls
          answering to the same string, in the DOM at the same time, and a reader hearing "Close
          menu, expanded" for a button that is about to close something already open. */}
      <button
        ref={burgerRef}
        className="nav__burger"
        onClick={toggleDrawer}
        aria-label="Menu"
        aria-expanded={isOpen}
        aria-controls={MOBILE_DRAWER_ID}
      >
        <BurgerIcon />
      </button>

      <div className={`nav__scrim${isOpen ? ' nav__scrim--open' : ''}`} onClick={closeDrawer} aria-hidden="true" />
      {/* `<aside>` is a `complementary` landmark whatever it holds, so an unnamed one appeared in
          the landmark list of every mobile page. Named, it reads as the surface the burger opens. */}
      <aside
        id={MOBILE_DRAWER_ID}
        className={`nav__drawer${isOpen ? ' nav__drawer--open' : ''}`}
        aria-label="Site menu"
      >
        <DrawerTop isFlatDrawer={isFlatDrawer} onClose={closeDrawer} />
        <DrawerBody
          searchKey={search.searchKey}
          items={items}
          isSearchEnabled={search.isEnabled}
          isSearching={search.isSearching}
          expandedIds={expandedIds}
          onSearchActiveChange={search.setIsSearching}
          onToggleAccordion={toggleAccordion}
          onNavigate={closeDrawer}
        />
      </aside>
    </nav>
  );
}

/**
 * The drawer's contents: the search field, then either its results or the nav accordion.
 *
 * Results take the place of the nav list rather than pushing it down — the drawer is one screen
 * tall, and a fan who is searching is not also browsing. Extracted so `MobileNav` stays under the
 * function-length and complexity thresholds.
 */
/** The drawer's brand row: sponsor-or-logo lockup and the ✕ that closes it. */
function DrawerTop({ isFlatDrawer, onClose }: { isFlatDrawer?: boolean; onClose: () => void }) {
  return (
    <div className={`nav__drawer-top${isFlatDrawer ? ' nav__drawer-top--flat' : ''}`}>
      <DrawerLogo />
      <button className="nav__drawer-close" onClick={onClose} aria-label="Close menu">
        <CloseIcon />
      </button>
    </div>
  );
}

function DrawerBody({
  searchKey,
  items,
  isSearchEnabled,
  isSearching,
  expandedIds,
  onSearchActiveChange,
  onToggleAccordion,
  onNavigate,
}: {
  /** Changing this remounts the search, dropping the term it holds — see `resetSearch`. */
  searchKey: number;
  items: ActiveNavigationNode[];
  isSearchEnabled: boolean;
  isSearching: boolean;
  expandedIds: Set<string>;
  onSearchActiveChange: (active: boolean) => void;
  onToggleAccordion: (id: string) => void;
  onNavigate: () => void;
}) {
  return (
    <>
      {/* Never focused on open. The drawer is reached from the burger, and moving focus into the
          field there pops the on-screen keyboard over the nav list someone opened to browse. */}
      {isSearchEnabled && (
        <GlobalSearch key={searchKey} variant="drawer" onActiveChange={onSearchActiveChange} onClose={onNavigate} />
      )}
      {!isSearching && (
        <div className="nav__drawer-body">
          {items.map((item) => (
            <AccordionItem
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onToggle={() => onToggleAccordion(item.id)}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Back button at the left edge of the band — sub-pages only, so users can
 * navigate back without browser chrome.
 */
function BandBackButton() {
  const location = useLocation();
  const navigate = useNavigate();

  const goBack = useCallback(() => {
    // If there's history, go back; otherwise navigate to home
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [navigate]);

  if (location.pathname === '/') {
    return null;
  }

  return (
    <button className="nav__back" onClick={goBack} aria-label="Go back">
      <BackIcon />
    </button>
  );
}

/** Brand logo in the drawer top band: sponsor lockup, site logo fallback. */
function DrawerLogo() {
  const { data: mainSponsor } = useMainSponsorQuery();
  const hasSponsorLogo = !!(mainSponsor?.assets?.[0]?.src || mainSponsor?.logo);
  return <div className="nav__drawer-logo">{hasSponsorLogo ? <MainSponsorLogo /> : <Logo />}</div>;
}

function BandLink({ item }: { item: ActiveNavigationNode }) {
  const activeClass = item.isActive ? ' nav__link--active' : '';
  if (!item.url) {
    return <span className={`nav__link small-text${activeClass}`}>{item.label}</span>;
  }
  return (
    <Link
      className={`nav__link small-text${activeClass}`}
      style={navItemColorStyle(item.color)}
      to={toHref(item.url)}
      {...linkTargetProps(item.sameTab)}
    >
      {item.label}
    </Link>
  );
}

type AccordionItemProps = {
  item: ActiveNavigationNode;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
};

function AccordionItem({ item, isExpanded, onToggle, onNavigate }: AccordionItemProps) {
  const hasChildren = !!item.children && item.children.length > 0;

  if (!hasChildren) {
    return (
      <div className="nav__acc-item">
        <AccordionRootLink item={item} onNavigate={onNavigate} />
      </div>
    );
  }

  return (
    <div className="nav__acc-item">
      <button className="nav__acc-row" onClick={onToggle} aria-expanded={isExpanded}>
        <span>{item.label}</span>
        <span className={`nav__acc-chev${isExpanded ? ' nav__acc-chev--open' : ''}`}>
          <ChevronIcon />
        </span>
      </button>
      <div className={`nav__acc-panel${isExpanded ? ' nav__acc-panel--open' : ''}`}>
        <div className="nav__acc-panel-inner">
          {item.children!.map((child) =>
            child.url ? (
              <Link
                key={child.id}
                className={`nav__acc-link small-text${child.isActive ? ' nav__acc-link--active' : ''}`}
                to={toHref(child.url)}
                {...linkTargetProps(child.sameTab)}
                onClick={onNavigate}
              >
                {child.label}
              </Link>
            ) : (
              <span key={child.id} className="nav__acc-link small-text">
                {child.label}
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function AccordionRootLink({ item, onNavigate }: { item: ActiveNavigationNode; onNavigate: () => void }) {
  const activeClass = item.isActive ? ' nav__acc-link--active' : '';
  if (!item.url) {
    return <span className={`nav__acc-link nav__acc-link--root${activeClass}`}>{item.label}</span>;
  }
  return (
    <Link
      className={`nav__acc-link nav__acc-link--root${activeClass}`}
      style={navItemColorStyle(item.color)}
      to={toHref(item.url)}
      {...linkTargetProps(item.sameTab)}
      onClick={onNavigate}
    >
      {item.label}
    </Link>
  );
}

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BurgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
